import { contentDelete, contentGet, contentPost, contentPut } from '../email-activity/content-api'
import { authDelete } from '../sso-workspace/auth-api'
import { isSilentViewError } from './entry-format'
import { viewerTimeZone } from '../date/local-time'

// The audit trail's read API on rapid-api-content (spec §4.7; phase 4, and
// phase 5 Task 1). Each resolves to the body and throws the response on
// refusal ({ status, data: { code, message } }), so serverMessage(err) /
// auditErrorText(err) show the server's own sentence when it is coded.
//
// List parameters (topic, category, recordType, app, country, actor, and
// Task 1's action and actorGroup) may be arrays: queryString joins them with
// commas, which is the form the server parses.

const enc = encodeURIComponent

// GET /entries -> { items: Entry[], next, from, to, geoUnavailable }
export const getEntries = params => contentGet('audit/entries', params)
// GET /entries/:id -> Entry (every field row); 404 audit.not_found
export const getEntry = (id, params) => contentGet(`audit/entries/${enc(id)}`, params)
// The reader's time zone (tz=, owner 2026-10-08): content counts hours and
// days in it, and names the zone it counted in (timeZone).
const withZone = params => ({ ...(params || {}), tz: viewerTimeZone() })

// GET /facets -> { topics, categories, actors, recordTypes, apps, countries, days, geoUnavailable, truncated,
// timeZone }: days are the reader's (tz=). 503 audit.busy when the instance is already preparing four summaries.
export const getFacets = params => contentGet('audit/facets', withZone(params))
// GET /session?sessionID=&<scope and window>[&others=1] -> { current, app, startedAt, firstSeenAt,
// otherOrganizations?, otherOrganizationsMore? } (content session.go): one session in words, read in
// the reader's scope; others=1 adds how many of its entries are in other organizations (a number only).
export const getSession = params => contentGet('audit/session', params)
// GET /catalogue -> { categories, actions, tables: [{table, label, topic, parent, columnLabels, platform}], etag }
export const getCatalogue = () => contentGet('audit/catalogue')
// GET /capabilities -> { orgAuditLog, sysAuditLog, requestLog, retention, table, export, signOutSessions? }
export const getCapabilities = () => contentGet('audit/capabilities')

// POST /views -> 204. One "viewed" line per person per record per day.
export const recordView = (recordType, recordID) => contentPost('audit/views', { recordType, recordID: String(recordID) })

// recordView for a page visit: 400, 404, 413 and 429 are never shown
// (isSilentViewError); anything else still throws.
export const noteView = async (recordType, recordID) => {
  try {
    await recordView(recordType, recordID)
  } catch (err) {
    if (!isSilentViewError(err)) throw err
  }
}

// The export worker reads params as map[string]string: lists are joined
// with commas, empty values dropped.
const stringParams = params => {
  const out = {}
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return
    if (Array.isArray(v) && v.length === 0) return
    out[k] = Array.isArray(v) ? v.join(',') : String(v)
  })
  return out
}

// POST /exports -> 202 { id, status }
export const startExport = (format, params) => contentPost('audit/exports', { format, params: stringParams(params) })
// GET /exports/:id -> { id, status, format, rowCount, truncated, createdAt, finishedAt, expiresAt, url? }
export const getExport = id => contentGet(`audit/exports/${enc(id)}`)
// GET /exports -> [{ id, status, format, rowCount, truncated, createdAt, finishedAt, expiresAt }]
// (the caller's exports from the last 7 days, newest first, at most 20; no
// link: a done export's 15-minute link comes only from GET /exports/:id).
export const listExports = () => contentGet('audit/exports')
// GET /health (SYS_AUDIT_LOG) -> { since, items: [{ key, count, severity: failure|warning }] }; only problems
export const getAuditHealth = () => contentGet('audit/health')

// GET /retention -> { platform, organizations, bounds }
export const getRetention = () => contentGet('audit/retention')
export const putRetention = settings => contentPut('audit/retention', settings)
export const putOrgRetention = (orgID, body) => contentPut(`audit/retention/organizations/${enc(orgID)}`, body)
// DELETE ...?reason= (a held override needs a reason to lift the hold)
export const deleteOrgRetention = (orgID, reason) => contentDelete(`audit/retention/organizations/${enc(orgID)}`, { reason })

// "Sign out this session" (owner ruling 6): auth's
// DELETE /api/v1/auth-session/by-session/:sessionID (auth 4d9af2f) ends a
// session by the entry's sessionID, auth_claims.session_id (a UUID) -- never
// the numeric /auth-session/:id. 200 on success; a coded 404
// auth.session_not_found for an unknown, ended, other organization's or (for
// a non-sys caller) staff/impersonation session; a coded 403
// auth.impersonation_not_allowed while impersonating; uncoded 403 without
// permission.
export const signOutSession = sessionID => authDelete(`auth-session/by-session/${enc(sessionID)}`)
